import React, { useCallback, useEffect, useRef } from 'react';
import type { QuestionType } from '../../../services/api';
import { useLocale } from "../../../locales";

interface QuestionTypeChangeConfirmModalProps {
  currentQuestionType?: string;
  newQuestionType: QuestionType;
  elementCount: number;
  onConfirm: () => void;
  onCancel: () => void;
}

const QuestionTypeChangeConfirmModal: React.FC<QuestionTypeChangeConfirmModalProps> = ({
  currentQuestionType,
  newQuestionType,
  elementCount,
  onConfirm,
  onCancel,
}) => {
  const { t } = useLocale();
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    contentRef.current?.focus();
  }, []);

  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCancel();
      }
    };

    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, [onCancel]);

  const handleConfirm = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      onConfirm();
    },
    [onConfirm]
  );

  return (
    <div className="qe-modal" role="presentation">
      <div className="qe-modal__backdrop" onClick={onCancel} />
      <div className="qe-modal__content qe-modal__content--warning" ref={contentRef} tabIndex={-1}>
        <form onSubmit={handleConfirm}>
          <div className="qe-modal__header">
            <div className="qe-modal__header-icon">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className="qe-modal__header-content">
              <h2 className="qe-modal__title">{t("questionTypeChange.title")}</h2>
              <p className="qe-modal__subtitle">{t("questionTypeChange.irreversible")}</p>
            </div>
          </div>

          <div className="qe-modal__body">
            <div className="qe-modal__info-box">
              <div className="qe-modal__info-icon">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10 10V14M10 6H10.01M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className="qe-modal__info-content">
                <p className="qe-modal__info-text">
                  {t("questionTypeChange.elementCount").replace("{count}", String(elementCount))}
                </p>
              </div>
            </div>

            <div className="qe-modal__change-info">
              <p className="qe-modal__change-text">
                {t("questionTypeChange.changeInfo")}
              </p>
              <div className="qe-modal__type-change">
                <div className="qe-modal__type-badge qe-modal__type-badge--current">
                  <span className="qe-modal__type-label">{t("questionTypeChange.currentType")}</span>
                  <span className="qe-modal__type-name">
                    {currentQuestionType || t("questionTypeChange.notSpecified")}
                  </span>
                </div>
                <div className="qe-modal__type-arrow">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7.5 15L12.5 10L7.5 5"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div className="qe-modal__type-badge qe-modal__type-badge--new">
                  <span className="qe-modal__type-label">{t("questionTypeChange.newType")}</span>
                  <span className="qe-modal__type-name">
                    {newQuestionType.displayName || newQuestionType.name}
                  </span>
                </div>
              </div>
            </div>

            <div className="qe-modal__warning-box">
              <div className="qe-modal__warning-icon">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10 6V10M10 14H10.01M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className="qe-modal__warning-content">
                <strong className="qe-modal__warning-title">{t("questionTypeChange.warning")}</strong>
                <p className="qe-modal__warning-text">
                  {t("questionTypeChange.warningMessage")}
                </p>
              </div>
            </div>
          </div>

          <div className="qe-modal__footer">
            <button
              type="button"
              className="qe-button qe-button--secondary"
              onClick={onCancel}
            >
              {t("questionTypeChange.cancel")}
            </button>
            <button
              type="submit"
              className="qe-button qe-button--primary qe-button--danger"
            >
              {t("questionTypeChange.confirmAndChange")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuestionTypeChangeConfirmModal;
